import { Badge, Box, Button, Card, Input, Separator, StyleProps, SubTitle, styled } from "@shared/primitive-components"
import { FormEvent, useEffect, useRef, useState } from "react"
import { usePolicyAndFleet } from "../../../contexts"
import { useAskJayBridge } from "../../../hooks/useAskJayBridge"
import { TEST_IDS } from "./fleetListTestIds"
import * as styles from "./styles/askJayBarStyles"

// Same cap the parent applies to a question arriving from outside the chat.
const MAX_QUESTION_LENGTH = 500

const Form = styled.form((props: StyleProps) => styles.form(props))

const SuggestionChip = styled.button((props: StyleProps) => styles.suggestion(props))

export const AskJayBar = () => {
  const { policy } = usePolicyAndFleet()
  // Keyed so a policy switch starts a fresh bar: empty question, new impression.
  return <AskJayBarForPolicy key={policy.id} policyId={policy.id} />
}

const AskJayBarForPolicy = ({ policyId }: Readonly<{ policyId: string }>) => {
  const { config, ask, reportShown } = useAskJayBridge(policyId)
  const [question, setQuestion] = useState(``)
  const [clearInput, setClearInput] = useState(false)

  const reported = useRef(false)
  useEffect(() => {
    if (config === null || reported.current) return
    reported.current = true
    reportShown()
  }, [config, reportShown])

  if (config === null) return null

  const trimmed = question.trim()

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!trimmed) return
    ask({ input: `typed`, prompt: trimmed })
    setQuestion(``)
    setClearInput(true)
  }

  return (
    <Card css={styles.card} data-testid={TEST_IDS.ASK_JAY.BAR}>
      <Box>
        <Box css={styles.header}>
          <SubTitle css={styles.title}>ASK JAY</SubTitle>
          <Badge variant="default">Beta</Badge>
        </Box>
        <Separator size="block" color="yellow" />
      </Box>

      <Form onSubmit={onSubmit}>
        <Box css={styles.inputWrap}>
          <Input
            variant="form"
            // The shared Input keeps its own text and reads this only when it mounts, so a bar
            // that hides and reappears shows the question it still holds rather than an empty box.
            value={question}
            onChange={setQuestion}
            clearInputControls={{ clearInput, setClearInput }}
            placeholder="Ask Jay about your fleet, cover dates or connectivity..."
            aria-label="Ask Jay a question"
            maxLength={MAX_QUESTION_LENGTH}
            dataTestId={TEST_IDS.ASK_JAY.INPUT}
          />
        </Box>
        <Button type="submit" css={styles.submit} disabled={!trimmed} data-testid={TEST_IDS.ASK_JAY.SUBMIT}>
          Ask Jay
        </Button>
      </Form>

      {config.suggestions.length > 0 && (
        <Box css={styles.suggestions}>
          {config.suggestions.map((suggestion) => (
            <SuggestionChip
              key={suggestion.id}
              type="button"
              onClick={() => ask({ input: `suggestion`, suggestionId: suggestion.id })}
              data-testid={TEST_IDS.ASK_JAY.SUGGESTION}
            >
              {suggestion.label}
            </SuggestionChip>
          ))}
        </Box>
      )}
    </Card>
  )
}
